/**
 * SoleadLab 2.0 — Asset State Card (C2.5)
 *
 * Inline component that fetches and displays the latest state snapshot for a
 * single asset node. All displayed values (condition, performance, risk,
 * confidence, summary text) are lifted verbatim from the API response — no
 * frontend re-derivation.
 *
 * Props:
 *   nodeId  — asset_nodes.id (UUID)
 *   siteId  — must match stateStore.currentSiteId; passed explicitly so the
 *             card can be rendered without assuming store initialization order
 */

import { useEffect } from 'react';
import { useStateStore } from '@/stores/stateStore';
import type { AssetSummaryJSON, ConditionStatus, PerformanceStatus, RiskStatus } from '@/types/state';
import '@/styles/state.css';

interface AssetStateCardProps {
  nodeId: string;
  siteId: string;
}

type AnyStatus = ConditionStatus | PerformanceStatus | RiskStatus;

function StatusBadge({ status }: { status: AnyStatus }) {
  return <span className={`state-badge state-badge--${status}`}>{status}</span>;
}

export default function AssetStateCard({ nodeId, siteId }: AssetStateCardProps) {
  const setSiteId = useStateStore((s) => s.setSiteId);
  const currentSiteId = useStateStore((s) => s.currentSiteId);
  const assetCache = useStateStore((s) => s.assetCache);
  const loadAssetLatest = useStateStore((s) => s.loadAssetLatest);

  // Ensure store is scoped to this site before fetching
  useEffect(() => {
    if (currentSiteId !== siteId) {
      setSiteId(siteId);
    }
  }, [siteId, currentSiteId, setSiteId]);

  useEffect(() => {
    if (currentSiteId === siteId) {
      loadAssetLatest(nodeId);
    }
  }, [nodeId, siteId, currentSiteId, loadAssetLatest]);

  const entry = assetCache[nodeId];
  const { latest, loading, error } = entry ?? { latest: null, loading: true, error: null };

  if (loading) {
    return (
      <div className="asset-state-card asset-state-card--loading">
        Loading state…
      </div>
    );
  }

  if (error) {
    return (
      <div className="asset-state-card asset-state-card--error">
        {error}
      </div>
    );
  }

  if (!latest) {
    return (
      <div className="asset-state-card asset-state-card--empty">
        No state snapshot yet.
      </div>
    );
  }

  let summary: AssetSummaryJSON | null = null;
  try { summary = JSON.parse(latest.summary_json); } catch { /* ignore */ }

  const confidenceLevel =
    latest.confidence_score >= 0.7 ? 'high' :
    latest.confidence_score >= 0.4 ? 'medium' : 'low';

  return (
    <div className="asset-state-card">
      <div className="asset-state-card__badges">
        <StatusBadge status={latest.condition_status} />
        <StatusBadge status={latest.performance_status} />
        <StatusBadge status={latest.risk_status} />
      </div>

      <div className="state-confidence" style={{ marginTop: 6 }}>
        <div className="state-confidence__bar">
          <div
            className={`state-confidence__fill state-confidence__fill--${confidenceLevel}`}
            style={{ width: `${Math.round(latest.confidence_score * 100)}%` }}
          />
        </div>
        <span>{Math.round(latest.confidence_score * 100)}% conf.</span>
      </div>

      {summary?.primary_signal && (
        <div className="asset-state-card__signal">{summary.primary_signal}</div>
      )}

      {summary && (
        <div className="asset-state-card__meta">
          {(summary.active_finding_count ?? 0) > 0 && (
            <span className="asset-state-card__finding-count">
              {summary.active_finding_count} finding{summary.active_finding_count !== 1 ? 's' : ''}
              {(summary.critical_finding_count ?? 0) > 0 && ` (${summary.critical_finding_count} critical)`}
            </span>
          )}
        </div>
      )}

      <div className="asset-state-card__ts">
        {new Date(latest.snapshot_ts).toLocaleString(undefined, {
          month: 'short', day: 'numeric',
          hour: '2-digit', minute: '2-digit',
        })}
      </div>
    </div>
  );
}
